import { useMemo, useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { Select2 } from '@/components/forms/Select2';
import { AsyncButton } from '@/components/common/AsyncButton';
import { revShareDealsApi } from '@/api/services/revenueShare';
import { useEnumOptions } from '@/features/common/enums/useEnumOptions';
import {
  MONTH_OPTIONS,
  type ConnectorOptionDto,
  type RevShareConfigDto,
  type SaveRevShareConfigInput,
} from '@/api/types/revenueShare';

interface Props {
  mode: 'create' | 'edit';
  row?: RevShareConfigDto;
  connectors: ConnectorOptionDto[];
  onClose: () => void;
  onSuccess: (verb: string) => void;
}

function yearOptions(back: number) {
  const now = new Date().getFullYear();
  const out: { value: number; label: string }[] = [];
  for (let y = now - back; y <= now; y++) out.push({ value: y, label: String(y) });
  return out;
}

export function RevShareDealFormModal({ mode, row, connectors, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const [connectorId, setConnectorId] = useState<string>(row ? String(row.dataSourceId) : '');
  const [login, setLogin] = useState<string>(row ? String(row.login) : '');
  const [revshare, setRevshare] = useState<string>(row ? String(row.revSharePercentage) : '');
  const [hwm, setHwm] = useState<string>(row ? String(row.highWaterMark) : '');
  const [periodType, setPeriodType] = useState<string>(row ? (row.periodType === 'Quarterly' ? '1' : '0') : '0');
  const [hwmYear, setHwmYear] = useState<string>(row ? String(row.hwmYear) : '');
  const [hwmMonth, setHwmMonth] = useState<string>(row?.hwmMonth != null ? String(row.hwmMonth) : '');
  const [quarter, setQuarter] = useState<string>(row?.quarter != null ? String(row.quarter) : '');
  const [includeCommissions, setIncludeCommissions] = useState(row?.includeCommissions ?? false);
  const [includeSwaps, setIncludeSwaps] = useState(row?.includeSwaps ?? false);
  const selectedConnector = connectors.find((c) => String(c.id) === connectorId);
  const isTerminal = isEdit ? (row?.isTerminal ?? false) : (selectedConnector?.isTerminal ?? false);
  const [activeLp, setActiveLp] = useState(isTerminal && (row?.activeLprsPnlConfiguration ?? false));
  const [touched, setTouched] = useState(false);

  const { options: enums } = useEnumOptions(['RevenueSharePeriodType', 'RevenueShareQuarter']);
  const periodTypeOptions = enums.RevenueSharePeriodType ?? [];
  const quarterOptions = enums.RevenueShareQuarter ?? [];

  const isQuarterly = periodType === '1';
  const years = useMemo(() => yearOptions(isEdit ? 5 : 2), [isEdit]);

  const onConnectorChange = (v: string | null) => {
    const id = v ?? '';
    setConnectorId(id);
    const c = connectors.find((x) => String(x.id) === id);
    if (c?.isTerminal) {
      setLogin(c.accountLogin != null ? String(c.accountLogin) : '');
    } else {
      setLogin('');
      setActiveLp(false);
    }
  };

  const revErr = revshare === '' || !(Number(revshare) > 0 && Number(revshare) <= 100) ? 'RevShare % must be between 0 and 100.' : '';
  const hwmErr = hwm === '' || !(Number(hwm) >= 0) ? 'High Water Mark must be zero or greater.' : '';
  const yearErr = hwmYear === '' ? 'HWM Year is required.' : '';
  const periodErr = isQuarterly ? (quarter === '' ? 'Quarter is required.' : '') : (hwmMonth === '' ? 'Month is required.' : '');
  const valid = !revErr && !hwmErr && !yearErr && !periodErr;

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    const payload: SaveRevShareConfigInput = {
      platformId: isEdit ? (row?.dataSourceId ?? 0) : (Number(connectorId) || 0),
      login: isEdit ? (row?.login ?? 0) : (Number(login) || 0),
      revSharePercentage: Number(revshare),
      highWaterMark: Number(hwm),
      periodType: Number(periodType),
      quarter: isQuarterly ? Number(quarter) : null,
      hwmMonth: isQuarterly ? null : Number(hwmMonth),
      hwmYear: Number(hwmYear),
      includeCommissions,
      includeSwaps,
      activeLprsPnlConfiguration: activeLp,
    };
    if (isEdit) {
      await revShareDealsApi.update({ ...payload, id: row!.id });
      onSuccess('Revenue Share Configuration Updated Successfully');
    } else {
      await revShareDealsApi.create(payload);
      onSuccess('Revenue Share Configuration Added Successfully');
    }
  };

  return (
    <Modal
      show
      title={isEdit ? 'Update' : 'Create'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light btn-sm" onClick={onClose}>Close</button>
          <AsyncButton className="btn btn-primary btn-sm" onClick={submit}>{isEdit ? 'Save' : 'Create'}</AsyncButton>
        </>
      }
    >
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label">Connector</label>
          {isEdit ? (
            <input className="form-control" value={row?.connectorName ?? ''} disabled />
          ) : (
            <Select2 placeholder="N/A" allowClear value={connectorId} options={connectors.map((c) => ({ value: c.id, label: c.name }))} onChange={onConnectorChange} />
          )}
        </div>
        <div className="col-md-6">
          <label className="form-label">Login</label>
          <input className="form-control" inputMode="numeric" value={login} disabled={isEdit || isTerminal} onChange={(e) => setLogin(e.target.value)} />
        </div>
        <div className="col-md-6">
          <label className="form-label">RevShare % <span className="text-danger">*</span></label>
          <input type="number" step="0.01" min="0" className={`form-control${touched && revErr ? ' is-invalid' : ''}`} value={revshare} onChange={(e) => setRevshare(e.target.value)} />
          {touched && revErr && <div className="invalid-feedback d-block">{revErr}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">High Water Mark <span className="text-danger">*</span></label>
          <input type="number" step="0.01" min="0" className={`form-control${touched && hwmErr ? ' is-invalid' : ''}`} value={hwm} onChange={(e) => setHwm(e.target.value)} />
          {touched && hwmErr && <div className="invalid-feedback d-block">{hwmErr}</div>}
        </div>
        <div className="col-md-6">
          <label className="form-label">Period Type</label>
          <Select2 searchable={false} allowClear={false} value={periodType} options={periodTypeOptions} onChange={(v) => setPeriodType(v ?? '0')} />
        </div>
        <div className="col-md-6">
          <label className="form-label">HWM Year <span className="text-danger">*</span></label>
          <Select2 searchable={false} allowClear={false} placeholder="Select…" value={hwmYear} options={years} onChange={(v) => setHwmYear(v ?? '')} />
          {touched && yearErr && <div className="text-danger small mt-1">{yearErr}</div>}
        </div>
        {isQuarterly ? (
          <div className="col-md-6">
            <label className="form-label">HWM Quarter <span className="text-danger">*</span></label>
            <Select2 searchable={false} allowClear={false} placeholder="Select…" value={quarter} options={quarterOptions} onChange={(v) => setQuarter(v ?? '')} />
            {touched && periodErr && <div className="text-danger small mt-1">{periodErr}</div>}
          </div>
        ) : (
          <div className="col-md-6">
            <label className="form-label">HWM Month <span className="text-danger">*</span></label>
            <Select2 searchable={false} allowClear={false} placeholder="Select…" value={hwmMonth} options={MONTH_OPTIONS} onChange={(v) => setHwmMonth(v ?? '')} />
            {touched && periodErr && <div className="text-danger small mt-1">{periodErr}</div>}
          </div>
        )}
        <div className="col-12 d-flex flex-wrap gap-4">
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id="rs-comm" checked={includeCommissions} onChange={(e) => setIncludeCommissions(e.target.checked)} />
            <label className="form-check-label" htmlFor="rs-comm">Include Commissions</label>
          </div>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id="rs-swap" checked={includeSwaps} onChange={(e) => setIncludeSwaps(e.target.checked)} />
            <label className="form-check-label" htmlFor="rs-swap">Include Swaps</label>
          </div>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id="rs-lp" checked={activeLp} disabled={!isTerminal} onChange={(e) => setActiveLp(e.target.checked)} />
            <label className="form-check-label" htmlFor="rs-lp">Active LP RS Profile</label>
          </div>
        </div>
      </div>
    </Modal>
  );
}
